import { 
  Brain, Globe, PoundSterling, Heart, Users, TrendingDown,
  Zap, FileCheck, MapPin, Shield, Calculator, Plane,
  Workflow, Bitcoin, Sparkles, BarChart3, Globe2,
  Lock, Server, Eye, AlertTriangle, Cloud, HardDrive,
  Stamp, Banknote, ShieldCheck, Building2, Receipt, Languages,
  Scale, Plug, FileSpreadsheet, CalendarRange, FileSignature, ShieldAlert,
  Target, Activity, Rocket, Database, Network, CheckCircle
} from 'lucide-react';
import { ProductFeatureConfig } from './types';

export const globalEmployerOfRecordFeatures: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Brain,
      title: "AI-Powered Compliance Engine",
      description: "Predictive compliance monitoring across 200+ jurisdictions with 98% accuracy. Auto-generates risk scorecards and penalty avoidance recommendations.",
      metrics: "98% Accuracy",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Globe,
      title: "Global Coverage Excellence", 
      description: "200+ countries coverage via owned entities and vetted partners. Real-time coverage dashboard with instant entity setup and compliance tracking.",
      metrics: "200+ Countries",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: PoundSterling,
      title: "Hyper-Personalized Payroll",
      description: "Intelligent currency hub with 0.2% forex spread, contextual payslips with AI explanations, and living wage calibration using local cost indices.",
      metrics: "0.2% Forex Spread",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Heart,
      title: "Talent-Centric Benefits",
      description: "Adaptive benefits marketplace with geo-contextual packages, global wellness wallet portable across 120 countries, and real-time utilization analytics.",
      metrics: "120 Countries",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: Users,
      title: "Autonomous Workforce Management",
      description: "Role-specific workspaces with multilingual chatbots, no-code workflow builder, and predictive capacity modeling for seamless operations.",
      metrics: "Zero-Code Setup",
      color: "from-purple-500 to-violet-600"
    },
    {
      icon: TrendingDown,
      title: "Value-Based Pricing Advantage",
      description: "40% cost advantage versus enterprise competitors with error-free guarantee. Transparent tiering: $299 Basic, $599 AI Pro, $899 Enterprise.",
      metrics: "40% Cost Savings",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: Zap,
      title: "Instant Global Onboarding",
      description: "24-hour employee onboarding with automated document processing, digital identity verification, and instant contract generation across all supported countries.",
      metrics: "24-Hour Setup",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Calculator,
      title: "AI-Powered Cost Optimisation",
      description: "Real-time salary benchmarking, tax optimisation recommendations, and automated cost reduction strategies that save 25% on global employment costs.",
      metrics: "25% Cost Reduction",
      color: "from-teal-500 to-emerald-600"
    },
    {
      icon: BarChart3,
      title: "Unified Global Reporting",
      description: "Single dashboard for compliance, payroll, and HR analytics across all countries with real-time insights and automated regulatory reporting.",
      metrics: "Real-Time Analytics",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Globe2,
      title: "Smart Entity Management",
      description: "AI-driven entity selection and optimisation for tax efficiency, compliance automation, and seamless cross-border employment facilitation.",
      metrics: "AI-Optimised",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: Stamp,
      title: "Visa & Work Permit Sponsorship",
      description: "End-to-end immigration processing, sponsorship, renewals, and dependents management with country-specific SLAs.",
      metrics: "End-to-end",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Banknote,
      title: "WPS & Payroll Compliance",
      description: "GCC-compliant WPS file generation, validation, and bank submissions with automated reconciliation & alerts.",
      metrics: "GCC WPS",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: ShieldCheck,
      title: "Social Security & Insurance Enrolment",
      description: "Automated enrolment and monthly remittance for pension, GOSI/SSS, health, and workers’ compensation.",
      metrics: "Automated",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Building2,
      title: "PRO & Government Relations",
      description: "Local PRO services for labor office, immigration, and ministry workflows with queueing and appointment orchestration.",
      metrics: "Local PRO",
      color: "from-purple-500 to-violet-600"
    },
    {
      icon: Receipt,
      title: "VAT-Compliant Invoicing & Local Billing",
      description: "Country-compliant invoicing, e-invoicing where mandated, and local currency billing with tax breakdowns.",
      metrics: "VAT Ready",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: Languages,
      title: "Bilingual Contracts & Support",
      description: "Arabic-English bilingual contracts, payslips, and support with legal-grade translations and attestation guidance.",
      metrics: "Arabic/English",
      color: "from-cyan-500 to-blue-600"
    }
  ],
  advancedCapabilities: [
    {
      icon: Zap,
      title: "Predictive Compliance AI",
      description: "Real-time regulation radar monitoring 10k+ government sources using NLP. Automatically flags regulatory changes 30 days before implementation.",
      metrics: "10k+ Sources",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: FileCheck,
      title: "Smart Contract Engine",
      description: "ML-trained regional contract templates with dynamic clause adjustments. Blockchain-based audit trails for all employment documents and changes.",
      metrics: "Blockchain Secured",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: MapPin,
      title: "Talent Match AI",
      description: "GPT-4 powered hiring location recommendations with cost optimization analysis. Suggests ideal markets for specific roles and skill requirements.",
      metrics: "GPT-4 Powered",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Shield,
      title: "Risk Simulation Engine",
      description: "Monte Carlo modeling of 50k+ compliance scenarios. Predicts financial exposure for regulatory changes with precision risk assessments.",
      metrics: "50k+ Scenarios",
      color: "from-red-500 to-pink-600"
    },
    {
      icon: Calculator,
      title: "Negotiation Coach AI",
      description: "Reinforcement learning from 1M+ EoR contracts provides real-time deal terms guidance and market-based counteroffer recommendations.",
      metrics: "1M+ Contracts",
      color: "from-green-500 to-teal-600"
    },
    {
      icon: Plane,
      title: "MENA Specialization Suite",
      description: "Automated WPS compliance for GCC, Sharia-compliant EOS calculator, Muqeem/Qiwa integration, and dedicated Emiratization quota management.",
      metrics: "GCC Optimized",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Workflow,
      title: "Embedded iPaaS Platform",
      description: "Pre-built connectors for 250+ HRIS/ERP systems with low-code workflow studio. Reduces integration time from weeks to under 48 hours.",
      metrics: "250+ Integrations",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Bitcoin,
      title: "Crypto Payroll Integration",
      description: "Licensed cryptocurrency payroll options in 50+ countries with automated compliance and real-time conversion capabilities.",
      metrics: "50+ Crypto Markets",
      color: "from-yellow-500 to-amber-600"
    },
    {
      icon: Sparkles,
      title: "Dynamic Benefits Builder",
      description: "AI-curated benefit packages that auto-adjust to local inflation and regulations. Includes innovative wellness NFTs and portable benefits.",
      metrics: "AI-Curated",
      color: "from-pink-500 to-violet-600"
    },
    {
      icon: BarChart3,
      title: "Predictive Analytics Suite",
      description: "Advanced workforce planning with absence prediction, performance correlation analysis, and talent retention probability modeling.",
      metrics: "ML Predictions",
      color: "from-emerald-500 to-green-600"
    },
    {
      icon: Globe2,
      title: "Global Mobility Management",
      description: "Comprehensive visa tracking, expatriate compliance automation, cross-border employment facilitation, and digital nomad qualification engine.",
      metrics: "Visa Automation",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Calculator,
      title: "LATAM Optimization Engine",
      description: "Digital nomad visa auto-qualification, unified aguinaldo bonus management across 18 countries, and regional tax optimization strategies.",
      metrics: "18 Countries",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Globe,
      title: "Immigration Orchestrator",
      description: "Centralized visa, work permit, sponsorship, and dependent management with renewal automation and SLA tracking across MENA.",
      metrics: "Full Lifecycle",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: Scale,
      title: "Final Settlement & EOS Calculator",
      description: "Region-aware end of service (EOS) and gratuity automation with country rules, accruals, and audit-ready breakdowns.",
      metrics: "Region Aware",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Plug,
      title: "Qiwa/Muqeem/MOHRE/Mudad Connectors",
      description: "Native integrations for GCC labor systems enabling contract attestations, transfer approvals, WPS submissions, and status sync.",
      metrics: "Native GCC",
      color: "from-green-500 to-teal-600"
    },
    {
      icon: FileSpreadsheet,
      title: "Automated WPS Builder & Reconciliation",
      description: "Auto-generate WPS files, validate bank schemas, and reconcile payroll disbursements with exception workflows.",
      metrics: "Auto-Validate",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: CalendarRange,
      title: "Leave, Holiday & Overtime Policy Engine",
      description: "Country calendars, overtime rules, and leave policies with automatic pro-rating, carryover, and public holiday handling.",
      metrics: "Policy Engine",
      color: "from-purple-500 to-violet-600"
    },
    {
      icon: FileSignature,
      title: "Document Attestation & Translation Tracker",
      description: "Track attestations, legalizations, and certified translations with chain-of-custody and SLA countdowns.",
      metrics: "Tracked",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: ShieldAlert,
      title: "AML/KYC & Sanctions Screening",
      description: "Automated employee and vendor screening against global sanctions lists with risk scoring and audit trails.",
      metrics: "Screened",
      color: "from-red-500 to-rose-600"
    }
  ],
  securityPerformance: [
    {
      icon: Target,
      title: "Compliance Excellence Framework",
      description: "SOC 2 Type II, ISO 27001, GDPR, and regional compliance certifications with continuous auditing and automated compliance monitoring across 200+ jurisdictions.",
      metrics: "SOC 2 + ISO 27001",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: Activity,
      title: "Security Performance Analytics", 
      description: "Advanced security metrics dashboard with real-time threat analysis, vulnerability assessment scoring, and predictive security intelligence powered by AI.",
      metrics: "Real-time Analytics",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Rocket,
      title: "Scalable Security Infrastructure",
      description: "Enterprise-grade platform supporting 100K+ users with auto-scaling security controls, distributed architecture, and elastic threat protection capabilities.",
      metrics: "100K+ Users",
      color: "from-purple-500 to-violet-600"
    },
    {
      icon: Shield,
      title: "Advanced Firewall & DDoS Protection",
      description: "Next-generation firewall with AI-powered threat detection, real-time DDoS mitigation, and intelligent traffic analysis protecting against sophisticated attacks.",
      metrics: "AI-Powered Protection",
      color: "from-red-500 to-rose-600"
    },
    {
      icon: Eye,
      title: "Multi-Factor Authentication Suite",
      description: "Enterprise SSO with biometric authentication, hardware security keys, adaptive risk-based authentication, and seamless integration with existing identity providers.",
      metrics: "Biometric + Hardware",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: Database,
      title: "Security Information & Event Management",
      description: "Comprehensive SIEM solution with behavioral analytics, automated incident response, threat intelligence correlation, and forensic investigation capabilities.",
      metrics: "Behavioral Analytics",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: HardDrive,
      title: "Data Loss Prevention & Backup",
      description: "Automated data classification, encryption at rest and in transit, geo-redundant backups, and intelligent data loss prevention with policy enforcement.",
      metrics: "Automated Protection",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Network,
      title: "Network Segmentation & Monitoring",
      description: "Zero-trust network architecture with micro-segmentation, continuous network monitoring, anomaly detection, and automated threat isolation protocols.",
      metrics: "Zero-Trust Network",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: CheckCircle,
      title: "Continuous Security Assessment",
      description: "Automated vulnerability scanning, penetration testing, security posture assessment, and continuous compliance validation with remediation workflows.",
      metrics: "Automated Scanning",
      color: "from-teal-500 to-emerald-600"
    }
  ]
};